import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import noop from 'lodash/noop';

import IconButton from '@adsk/bim360-matrix-react-icon-button';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import Tooltip from '@adsk/bim360-matrix-react-tooltip';
import MenuItemIcon from '@adsk/bim360-matrix-react-menu-item-icon';
import MenuItem from '@adsk/bim360-matrix-react-menu-item';
import Ul from '@adsk/bim360-matrix-react-ul';
import { ThemeConsumer, Themes } from '@adsk/bim360-matrix-react-helpers/es/theme';

import moreIcon from '@adsk/bim360-matrix-react-assets/more.svg';
import alloyMoreVertical from '@adsk/bim360-matrix-react-assets/alloy-more-vertical.svg';
import './_ActionButton.scss';

const { Places, TriggerEvents } = Tooltip;

const ActionDropdown = ({ style, children }) => {
  return (
    <div className="ActionButton__dropdown" style={style}>
      {children}
    </div>
  );
};

ActionDropdown.propTypes = {
  style: PropTypes.object,
  children: PropTypes.node
};

/**
 * ActionButton for the table
 */
const ActionButton = React.forwardRef(
  (
    { className, options, container, tooltipClassName, disabled, onSelect, onHide, onShow },
    ref
  ) => {
    if (!options || !options.length) return null;
    const [isOpen, setIsOpen] = React.useState(false);
    const defaultSvgProps = {
      height: '20px',
      width: '20px'
    };

    const handleHide = () => {
      setIsOpen(false);
      onHide();
    };

    const handleClick = e => {
      e.stopPropagation();
      e.preventDefault();

      setIsOpen(true);
      onShow();
    };

    const handleSelect = eventKey => {
      onSelect(eventKey);
      handleHide();
    };

    return (
      <ThemeConsumer>
        {({ theme }) => (
          <div className={cn('ActionButton', `ActionButton--${theme}`, className)}>
            <Tooltip
              place={Places.BOTTOM}
              show={isOpen}
              rootClose={true}
              container={container}
              containerPadding={5}
              onHide={handleHide}
              tooltipClassName={cn(
                'ActionButton__popover-container',
                `ActionButton__popover-container--${theme}`,
                tooltipClassName
              )}
              trigger={TriggerEvents.CLICK}
              content={
                <Ul className="ActionButton__menu">
                  {options.map(
                    ({
                      key,
                      label,
                      disabled,
                      showTooltipOnDisable,
                      tooltipContent,
                      tooltipContainer = () => document.body,
                      tooltipPlace = Places.LEFT,
                      tooltipClassName,
                      ...svgProps
                    }) => {
                      const Item = !Object.keys(svgProps).length ? MenuItem : MenuItemIcon;
                      const content = showTooltipOnDisable
                        ? disabled
                          ? tooltipContent
                          : null
                        : tooltipContent;
                      return (
                        <Tooltip key={key} place={tooltipPlace} content={content} container={tooltipContainer} tooltipClassName={tooltipClassName}>
                          <Item
                            key={key}
                            eventKey={key}
                            disabled={disabled}
                            onClick={handleSelect}
                            svgProps={{ ...defaultSvgProps, ...svgProps }}
                          >
                            {label}
                          </Item>
                        </Tooltip>
                      );
                    }
                  )}
                </Ul>
              }
            >
              <IconButton
                className={cn('ActionButton__icon', { 'ActionButton__icon--open': isOpen })}
                disabled={disabled}
                onClick={handleClick}
              >
                {theme === Themes.ALLOY ? (
                  <SvgIcon width="24px" height="24px">
                    {alloyMoreVertical}
                  </SvgIcon>
                ) : (
                  <SvgIcon>{moreIcon}</SvgIcon>
                )}
              </IconButton>
            </Tooltip>
          </div>
        )}
      </ThemeConsumer>
    );
  }
);

ActionButton.displayName = 'ActionButton';

ActionButton.defaultProps = {
  disabled: false,
  onSelect: noop,
  onHide: noop,
  onShow: noop
};

ActionButton.optionPropType = PropTypes.shape({
  /**
   * event key for the option
   */
  key: PropTypes.string.isRequired,
  /**
   * Label for the option
   */
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  /**
   * SvgId for the option
   */
  svgId: PropTypes.string,
  /**
   * An svg node (can be provided instead of an svgId)
   */
  svg: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  /**
   * Width of the svg icon
   */
  width: PropTypes.string,
  /**
   * height of the svg icon
   */
  height: PropTypes.string,
  /**
   * A boolean value for showing tooltip when option is disable
   */
  showTooltipOnDisable: PropTypes.bool,
  /**
   * Tooltip message
   */
  tooltipContent: PropTypes.string,
  /**
   * Tooltip place
   */
  tooltipPlace: PropTypes.oneOf(Object.values(Places)),
  /**
   * Tooltip container
   */
  tooltipContainer:   PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  /**
   * Class name for the options' tooltip component
   */
  tooltipClassName: PropTypes.string,
});

ActionButton.propTypes = {
  /**
   * Class name for the component
   */
  className: PropTypes.string,
  /**
   * Class name for the tooltip component
   */
  tooltipClassName: PropTypes.string,
  /**
   * A Node, Component instance, or function that returns either. The `container` will have the Popover appended to it.
   */
  container: PropTypes.oneOfType([PropTypes.object, PropTypes.func]).isRequired,
  /**
   * A boolean value for disabling the component
   */
  disabled: PropTypes.bool,
  /**
   * A callback function for the option click event
   * The handler is of the shape of "(key) => *"
   */
  onSelect: PropTypes.func.isRequired,
  /**
   * options for the action
   */
  options: PropTypes.arrayOf(ActionButton.optionPropType),
  /**
   * A callback function for options menu show event
   */
  onShow: PropTypes.func,
  /**
   * A callback function for options menu hide event
   */
  onHide: PropTypes.func
};

export default ActionButton;
